import React, { useEffect, useRef, useState } from "react";
import { ChevronUp, Menu } from "lucide-react";
import { useNavigate } from "react-router-dom";

const FloatingActionsMenu = ({
    actions = [],
    label = "Quick Actions",
    className = "",
    visible = true,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef(null);
    const navigate = useNavigate();
    const safeActions = Array.isArray(actions) ? actions : [];
    const hasActions = safeActions.length > 0;

    useEffect(() => {
        if (!visible) {
            setIsOpen(false);
        }
    }, [visible]);

    useEffect(() => {
        if (!isOpen) return undefined;

        const handlePointerDown = (event) => {
            if (!menuRef.current?.contains(event.target)) {
                setIsOpen(false);
            }
        };
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                setIsOpen(false);
            }
        };

        document.addEventListener("pointerdown", handlePointerDown);
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("pointerdown", handlePointerDown);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen]);

    if (!hasActions) return null;

    const handleActionClick = (action) => {
        if (action.disabled) return;

        setIsOpen(false);

        if (action.to) {
            navigate(action.to);
            return;
        }

        action.onClick?.();
    };

    return (
        <div
            ref={menuRef}
            aria-hidden={!visible}
            className={`fixed bottom-4 right-4 z-50 transition duration-200 ease-out sm:bottom-6 sm:right-6 ${
                visible
                    ? "translate-y-0 opacity-100"
                    : "pointer-events-none translate-y-3 opacity-0"
            } ${className}`}
        >
            {isOpen && (
                <div className="app-dropdown-enter absolute bottom-full right-0 mb-3 w-56 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
                    <div className="border-b border-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Page actions
                    </div>
                    <div className="py-1">
                        {safeActions.map((action) => {
                            const Icon = action.icon;

                            return (
                                <button
                                    key={action.label}
                                    type="button"
                                    onClick={() => handleActionClick(action)}
                                    disabled={action.disabled}
                                    title={action.title}
                                    className="group flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm font-semibold text-slate-700 transition hover:bg-indigo-50 hover:text-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {Icon && (
                                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 transition group-hover:bg-indigo-100">
                                            <Icon
                                                size={16}
                                                aria-hidden="true"
                                            />
                                        </span>
                                    )}
                                    <span className="min-w-0 truncate">
                                        {action.label}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            <button
                type="button"
                aria-label={isOpen ? "Close page actions" : "Open page actions"}
                aria-expanded={isOpen}
                onClick={() => setIsOpen((current) => !current)}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-indigo-200/60 transition hover:bg-indigo-700 focus:outline-none focus:ring-4 focus:ring-indigo-200"
            >
                <Menu size={17} aria-hidden="true" />
                {label}
                <ChevronUp
                    size={16}
                    aria-hidden="true"
                    className={`transition-transform ${
                        isOpen ? "rotate-180" : ""
                    }`}
                />
            </button>
        </div>
    );
};

export default FloatingActionsMenu;
